import { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { ColumnRender, IColumn, SortOrder } from "../../../components/UI/Declarations";
import { PagedListTable } from "../../../components/UI/PagedList/PagedListTable";
import "../../../components/UI/PagedList.css";
import { sortPreviewModalRows, type PreviewModalRow } from "./previewModalRows";

export interface RegDataPreviewFieldTableProps {
    fieldKey: string;
    rows: PreviewModalRow[];
    revertDisabled?: boolean;
    onRevertStaged: (changeKey: string) => void;
}

export function RegDataPreviewFieldTable({
    rows,
    revertDisabled = false,
    onRevertStaged,
}: RegDataPreviewFieldTableProps) {
    const [sortBy, setSortBy] = useState("values");
    const [sortOrder, setSortOrder] = useState(SortOrder.ascending);

    const sortedRows = useMemo(
        () => sortPreviewModalRows(rows, sortBy, sortOrder),
        [rows, sortBy, sortOrder]
    );

    const handleSortByClicked = useCallback(
        (member: string) => {
            if (sortBy === member) {
                setSortOrder(sortOrder === SortOrder.ascending ? SortOrder.descending : SortOrder.ascending);
            } else {
                setSortBy(member);
                setSortOrder(SortOrder.ascending);
            }
        },
        [sortBy, sortOrder]
    );

    const columns = useMemo<IColumn[]>(
        () => [
            {
                member: "values",
                title: "Value(s)",
                width: 420,
                renderAs: ColumnRender.text,
            },
            {
                member: "action",
                title: "Action",
                width: 120,
                align: "center",
                renderAs: ColumnRender.text,
            },
            {
                member: "revert",
                title: "",
                width: 100,
                canSort: false,
                align: "center",
                renderAs: ColumnRender.customRenderer,
                customRenderer: (item: PreviewModalRow) => (
                    <td className="text-center">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            aria-label="Revert pending change"
                            disabled={revertDisabled}
                            onClick={() => onRevertStaged(item.changeKey)}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                            Revert
                        </Button>
                    </td>
                ),
            },
        ],
        [onRevertStaged, revertDisabled]
    );

    const tableWidth = useMemo(
        () => columns.reduce((sum, column) => sum + column.width, 0),
        [columns]
    );

    return (
        <PagedListTable
            visibleColumns={columns}
            results={sortedRows}
            hasLoadError={false}
            loadErrorMessage={null}
            hasResults={sortedRows.length > 0}
            total={sortedRows.length}
            isBusy={false}
            tableWidth={tableWidth}
            sortBy={sortBy}
            sortOrder={sortOrder}
            primaryActions={[]}
            secondaryActions={[]}
            keyColumn="changeKey"
            onSortByClicked={handleSortByClicked}
            onSelectColumnsClicked={() => undefined}
            showSelectColumns={false}
            onScroll={() => undefined}
            getRowClassName={() => ""}
        />
    );
}
